/**
 * Réception guidée — une ligne à la fois (PWA-first, août 2026).
 *
 * Parcours demandé : scanner le code-barres → rapprochement catalogue ou
 * création → photo étiquette DLC/LOT → vérifier ce qui a été pré-rempli →
 * vérifier l'unité (vrac) → approuver.
 *
 * Le scan est le geste d'entrée, pas une étape parmi d'autres : depuis la liste
 * des lignes du bon, scanner un colis OUVRE sa ligne. C'est l'ordre réel du
 * travail — les colis sortent de la palette dans le désordre — et cela confirme
 * du même geste le rapprochement proposé par l'analyse du bon, ou le corrige.
 * Dans une ligne ouverte, le scan sert alors à corriger ce rapprochement.
 *
 * Deux différences de fond avec l'onglet :
 * - le n° de lot lu sur l'étiquette est PERSISTÉ (supplier_lot_number), donc
 *   recopié sur le lot par processLineToStock — l'onglet l'affichait sans jamais
 *   l'enregistrer, ce qui laissait un trou dans la traçabilité bio ;
 * - les champs édités sont enregistrés via PATCH /deliveries/lines/:id avant
 *   l'approbation. approveLine attend `unit_price_ttc_eur` et ignore
 *   silencieusement `unit_price_ttc_cents` : passer par updateDeliveryLine (qui
 *   convertit) évite de perdre un prix d'achat corrigé.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { Delivery, DeliveryLine, ProductSuggestion } from '../../services/api';
import { useTenant } from '../../contexts/TenantContext';
import {
  useBarcodeScan,
  isWedgeScannerEnabled,
  WEDGE_SCANNER_STORAGE_KEY,
} from '../../hooks/useBarcodeScan';
import { IS_TOUCH_DEVICE } from '../../utils/touchDevice';
import DLCInput from '../../components/ui/DLCInput';
import PriceInput from '../../components/ui/PriceInput';
import WebcamCaptureModal from '../../components/ui/WebcamCaptureModal';
import BarcodeScannerModal from '../../components/ui/BarcodeScannerModal';
import ProductAutocomplete from '../../components/ProductAutocomplete';
import { extractLabelLocal } from '../../services/camera/localRecognition';
import { prepareImageForUpload, readLabel, LabelInfo } from '../../utils/imagePrep';
import { formatQty } from '../../utils/pos.utils';
import { useInvitationNouveaute } from '../../components/reception/InvitationNouveaute';
import ArticleEnPlusModal from '../../components/reception/ArticleEnPlusModal';
import { eanConflit } from '../../utils/eanConflit';
import { alertePrixAchat } from '../../utils/prixAchatColis';

/** Prix de vente conseillé : achat TTC × 1,5 arrondi au 0,05 € supérieur (même règle que l'onglet). */
const suggestedSalePriceCents = (purchaseTtcCents: number): number =>
  Math.ceil((purchaseTtcCents * 1.5) / 5) * 5;

/** Motif consigne : une vidange facturée n'entre pas en stock, elle se refuse. */
const CONSIGNE_REASON = 'Consigne / vidange (pas un article à recevoir)';

const REJECT_REASONS = [
  'Produit endommagé',
  'Mauvaise qualité',
  'Quantité incorrecte',
  'DLC trop courte',
  'Produit manquant',
  CONSIGNE_REASON,
];

const UNITS = [
  { value: 'pièce', label: 'Pièce' },
  { value: 'kg', label: 'Kilo' },
  { value: 'L', label: 'Litre' },
];

interface ConfirmState {
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => void;
  /** Troisième voie : ni « oui » ni « j'abandonne » (ex. recevoir sans toucher au prix). */
  secondaryLabel?: string;
  onSecondary?: () => void;
}

/** Saisie d'une ligne, conservée tant que la ligne n'est pas traitée. */
interface Draft {
  dlc: string;
  lotNumber: string;
  quantity: string;
  unit: string;
  /** L'opérateur a choisi l'unité lui-même : un scan ne doit plus l'écraser. */
  unitTouched: boolean;
  purchaseCents: number;
  saleCents: number;
  saleEdited: boolean;
  markedAsNew: boolean;
  newName: string;
  /** Recherche catalogue libre (produits sans code-barres). */
  searchName: string;
  /**
   * Colis arrivé avec DEUX dates limites. Le modèle impose une DLC par lot :
   * on crée le lot normalement puis on en détache un jumeau. Se déclare ici
   * parce que c'est en ouvrant le colis qu'on le découvre.
   */
  secondLot: { quantity: string; dlc: string; lotNumber: string } | null;
  labelInfo: LabelInfo | null;
  /** L'opérateur a confirmé un prix d'achat que l'écran trouvait suspect. */
  prixAchatVerifie: boolean;
}

function seedDraft(line: DeliveryLine): Draft {
  const qty = line.received_quantity ?? line.quantity ?? line.ordered_quantity;
  const purchase = line.unit_price_ttc_cents ?? 0;
  return {
    dlc: line.default_expiry_date ? line.default_expiry_date.slice(0, 10) : '',
    lotNumber: line.supplier_lot_number || '',
    quantity: qty != null ? String(qty) : '',
    unit: line.unit || line.matched_product_unit || 'pièce',
    unitTouched: false,
    purchaseCents: purchase,
    // Produit déjà au catalogue : on part du prix RÉELLEMENT pratiqué. Afficher
    // le conseillé (achat ×1,5) donnait à croire qu'il s'appliquerait, alors que
    // sans confirmation explicite le prix de la caisse ne bouge pas.
    saleCents: line.matched_product_price_cents != null
      ? line.matched_product_price_cents
      : (purchase > 0 ? suggestedSalePriceCents(purchase) : 0),
    saleEdited: false,
    markedAsNew: false,
    newName: '',
    searchName: '',
    secondLot: null,
    labelInfo: null,
    prixAchatVerifie: false,
  };
}

export default function ReceptionGuideePage() {
  const { id: deliveryId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tenant } = useTenant();

  const [delivery, setDelivery] = useState<Delivery | null>(null);
  const [lines, setLines] = useState<DeliveryLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Ligne ouverte ; null = on est sur la liste des lignes du bon. */
  const [activeLineId, setActiveLineId] = useState<string | null>(null);
  /** Scan sans correspondance : à quelle ligne l'associer ? */
  const [orphanBarcode, setOrphanBarcode] = useState<string | null>(null);

  /**
   * Saisie en cours, INDEXÉE PAR LIGNE. Un seul jeu de champs serait plus court,
   * mais deux choses se produisent en dehors de la ligne affichée : l'OCR
   * d'étiquette met jusqu'à 12 s à répondre (l'opérateur a le temps de revenir à
   * la liste et d'ouvrir une autre ligne), et rouvrir une ligne doit retrouver ce
   * qui y a été tapé — rien n'est enregistré côté serveur avant l'approbation.
   * Les écritures différées ciblent donc l'identifiant capturé.
   */
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});

  // Rapprochement (indexés par ligne pour la même raison)
  const [suggestions, setSuggestions] = useState<Record<string, ProductSuggestion[]>>({});
  const [scanCandidates, setScanCandidates] = useState<Record<string, ProductSuggestion[]>>({});
  const [scanning, setScanning] = useState(false);

  // Photo / OCR
  const [uploadingPhotoLineId, setUploadingPhotoLineId] = useState<string | null>(null);
  const [extractingLineId, setExtractingLineId] = useState<string | null>(null);

  // Modales
  const [scannerOpen, setScannerOpen] = useState(false);
  /** Lignes traitées repliées par défaut : on les consulte pour corriger, pas pour travailler. */
  const [showDone, setShowDone] = useState(false);
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [otherReason, setOtherReason] = useState('');
  const [packSizeOpen, setPackSizeOpen] = useState(false);
  const [packSize, setPackSize] = useState('');
  /**
   * « conversion » : colis → kg (quantité × N, prix ÷ N) ; « prix » : seul le prix
   * d'achat est celui du colis, la quantité est déjà juste (cas Vajra, 04/11/2026).
   * `depuisUnite` : ouverte d'office parce qu'on vient de passer au Kilo.
   */
  const [packMode, setPackMode] = useState<{ mode: 'conversion' | 'prix'; depuisUnite?: boolean }>({ mode: 'conversion' });
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  // Invitation à annoncer une nouveauté, posée juste après la réception d'une ligne.
  const invitationNouveaute = useInvitationNouveaute();
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // Douchette branchée à un appareil tactile (iPhone comme iPad) : sans ce
  // réglage le hook de scan y reste inerte, le clavier virtuel iOS ressemblant
  // trop à une douchette.
  const [wedgeOnTouch, setWedgeOnTouch] = useState(isWedgeScannerEnabled);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Verrou d'approbation : `busy` est partagé avec le rapprochement, dont le
  // relâchement rouvrirait le bouton « Réceptionner » pendant qu'un envoi est en
  // vol — et approveLine n'est pas idempotent (deuxième lot créé).
  const approvingRef = useRef(false);
  /** Lignes déjà parties au serveur — protège des doublons quand le refetch échoue. */
  const approvedIdsRef = useRef<Set<string>>(new Set());

  // Lecture des lignes depuis un callback différé, sans le remettre en dépendance.
  const linesRef = useRef<DeliveryLine[]>([]);
  useEffect(() => { linesRef.current = lines; }, [lines]);

  const pendingLines = useMemo(() => lines.filter((l) => l.status === 'pending'), [lines]);
  // Écran = la liste des lignes du bon tant qu'aucune n'est ouverte. La ligne
  // s'ouvre par scan de l'article ou par appui, jamais par un curseur : à la
  // réception, les colis sortent de la palette dans le désordre.
  const currentLine: DeliveryLine | undefined = activeLineId
    ? lines.find((l) => l.id === activeLineId)
    : undefined;
  const doneCount = lines.length - pendingLines.length;

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast((t) => (t === msg ? null : t)), 2600);
  }, []);

  // ── Chargement ────────────────────────────────────────────────────────────
  // « + Article reçu en plus » : livré, mais absent du bon importé (reliquat…)
  const [articleEnPlusOuvert, setArticleEnPlusOuvert] = useState(false);

  const loadAll = useCallback(async () => {
    if (!tenant?.id || !deliveryId) return;
    setLoadError(null);
    try {
      const [d, l] = await Promise.all([
        api.getDelivery(tenant.id, deliveryId),
        api.getDeliveryLines(tenant.id, deliveryId),
      ]);
      setDelivery(d);
      setLines(l);
    } catch {
      setLoadError('Impossible de charger ce bon.');
    } finally {
      setLoading(false);
    }
  }, [tenant?.id, deliveryId]);

  useEffect(() => {
    document.title = 'Réception guidée — Folie Boutique';
    loadAll();
  }, [loadAll]);

  /**
   * Recharger en revenant sur l'écran. Une réception s'étale sur une matinée,
   * et l'état d'une ligne peut changer sans passer par cet appareil : autre
   * poste, ou remise à traiter par le serveur quand la création du stock a
   * échoué. L'écran affichait alors une ligne « traitée » que le serveur voyait
   * en attente — d'où un bouton « Annuler » qui répondait « pas encore
   * réceptionnée » (constaté le 12/08 sur l'oignon jaune).
   * Seulement sur la LISTE : recharger pendant la saisie d'une ligne
   * remplacerait ce que l'opérateur est en train de faire.
   */
  useEffect(() => {
    const refreshOnReturn = () => {
      if (document.visibilityState === 'visible' && !activeLineId) loadAll();
    };
    document.addEventListener('visibilitychange', refreshOnReturn);
    window.addEventListener('focus', refreshOnReturn);
    return () => {
      document.removeEventListener('visibilitychange', refreshOnReturn);
      window.removeEventListener('focus', refreshOnReturn);
    };
  }, [loadAll, activeLineId]);

  const currentLineId = currentLine?.id;

  // Liste et ligne ouverte partagent le défilement de la page. Ouvrir une ligne
  // (au scan surtout) depuis le bas de la liste laissait la page descendue :
  // le produit rattaché, en tête de la fiche, restait caché au-dessus (signalé
  // le 15/10/2026). On remonte en haut à l'ouverture, et le retour à la liste
  // retrouve l'endroit où l'on était.
  const defilementListeRef = useRef(0);
  const ligneOuverte = !!currentLineId;
  useEffect(() => {
    if (ligneOuverte) {
      window.scrollTo({ top: 0 });
    } else if (defilementListeRef.current > 0) {
      const y = defilementListeRef.current;
      requestAnimationFrame(() => window.scrollTo({ top: y }));
    }
  }, [ligneOuverte, currentLineId]);
  useEffect(() => {
    if (ligneOuverte) return;
    const noter = () => { defilementListeRef.current = window.scrollY; };
    window.addEventListener('scroll', noter, { passive: true });
    return () => window.removeEventListener('scroll', noter);
  }, [ligneOuverte]);

  // Semer le brouillon la première fois qu'on affiche une ligne. On ne le
  // réinitialise JAMAIS ensuite : ni un scan, ni un aller-retour de navigation
  // ne doit effacer ce que l'opérateur a tapé.
  useEffect(() => {
    if (!currentLine || !currentLineId) return;
    setDrafts((prev) => (prev[currentLineId] ? prev : { ...prev, [currentLineId]: seedDraft(currentLine) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLineId]);

  const draft: Draft | null = currentLine && currentLineId
    ? drafts[currentLineId] ?? seedDraft(currentLine)
    : null;

  // Prix d'achat suspect (prix d'un colis lu comme prix unitaire). Seulement pour
  // un produit déjà au catalogue : c'est lui qui porte un historique et un prix
  // de vente. Le repère « vente » est ignoré tant que l'opérateur a confirmé.
  const alerteAchat = currentLine && draft && currentLine.matched_product_id && !draft.markedAsNew
    && !draft.prixAchatVerifie
    ? alertePrixAchat({
        achatCents: draft.purchaseCents,
        habituelCents: currentLine.matched_product_usual_purchase_cents,
        venteCents: currentLine.matched_product_price_cents,
        quantite: parseFloat(draft.quantity.replace(',', '.')) || null,
      })
    : null;

  /** Modifie le brouillon d'une ligne DÉSIGNÉE (jamais « la ligne affichée »). */
  const patchDraft = useCallback((lineId: string, patch: Partial<Draft>) => {
    setDrafts((prev) => {
      // Le brouillon peut ne pas encore être semé (frappe dans le même tour de
      // rendu que l'affichage de la ligne) : on le sème depuis la ligne plutôt
      // que de perdre la saisie.
      const line = linesRef.current.find((l) => l.id === lineId);
      const base = prev[lineId] ?? (line ? seedDraft(line) : null);
      if (!base) return prev;
      return { ...prev, [lineId]: { ...base, ...patch } };
    });
  }, []);

  // Suggestions catalogue de la ligne courante
  useEffect(() => {
    if (!currentLineId) return;
    let cancelled = false;
    api.getLineProductMatches(currentLineId)
      .then((r) => {
        if (!cancelled) setSuggestions((prev) => ({ ...prev, [currentLineId]: r.matches || [] }));
      })
      .catch(() => {
        if (!cancelled) setSuggestions((prev) => ({ ...prev, [currentLineId]: [] }));
      });
    return () => { cancelled = true; };
  }, [currentLineId]);

  /**
   * Adopte l'unité de la fiche produit UNIQUEMENT pour combler un vide : si le bon
   * porte une unité, elle est conservée telle quelle, car la divergence bon/fiche
   * est exactement ce que le garde-fou « changement de nature » doit faire voir.
   * Lecture de `unitTouched` dans le réducteur : après un aller-retour réseau, la
   * closure du rendu ignorerait un choix fait entre-temps.
   */
  const adoptProductUnit = useCallback((
    lineId: string,
    productUnit?: string | null,
    productWeighted?: boolean | null,
  ) => {
    if (!productUnit) return;
    setDrafts((prev) => {
      const base = prev[lineId];
      const line = linesRef.current.find((l) => l.id === lineId);
      if (!base || base.unitTouched || line?.unit) return prev;
      return { ...prev, [lineId]: { ...base, unit: productWeighted ? 'kg' : productUnit } };
    });
  }, []);

  /**
   * Adopte le prix RÉELLEMENT pratiqué du produit qu'on vient de rattacher.
   *
   * Le brouillon est semé à l'ouverture de la ligne : si le produit n'est
   * rattaché qu'ensuite (scan, choix manuel), le champ garde le prix CONSEILLÉ
   * calculé sur l'achat — et comme l'opérateur n'y touche pas, rien n'est
   * appliqué alors qu'il lit un montant. C'est le cas signalé le 12/08 : un
   * prix conseillé inférieur à l'ancien, approuvé, resté sans effet ; il ne
   * s'appliquait qu'après avoir annulé et rouvert la ligne.
   * Sans effet si l'opérateur a déjà saisi un prix : son choix prime.
   */
  const adoptProductPrice = useCallback((lineId: string, priceCents?: number | null) => {
    if (priceCents == null) return;
    setDrafts((prev) => {
      const base = prev[lineId];
      if (!base || base.saleEdited) return prev;
      return { ...prev, [lineId]: { ...base, saleCents: priceCents } };
    });
  }, []);

  // ── Scan appliqué à une ligne : rattachement au produit du catalogue ───────
  const applyScanToLine = useCallback(async (lineId: string, barcode: string) => {
    if (!tenant?.id || !deliveryId) return;
    setScanning(true);
    setScanCandidates((prev) => ({ ...prev, [lineId]: [] }));
    try {
      const res = await api.scanLineBarcode(deliveryId, lineId, barcode);
      setLines((prev) => prev.map((l) => (l.id === lineId ? { ...l, ...res } : l)));
      if (res.scan_outcome === 'matched') {
        patchDraft(lineId, { markedAsNew: false });
        adoptProductUnit(lineId, res.matched_product_unit, res.matched_product_is_weighted);
        adoptProductPrice(lineId, res.matched_product_price_cents);
        showToast(`✓ ${res.matched_product_name || 'Produit'} reconnu`);
      } else if (res.scan_outcome === 'multiple') {
        setScanCandidates((prev) => ({ ...prev, [lineId]: res.scan_candidates || [] }));
        showToast('Plusieurs produits partagent ce code — choisissez');
      } else {
        setScanCandidates((prev) => ({ ...prev, [lineId]: res.scan_candidates || [] }));
        showToast('Code inconnu du catalogue');
      }
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Échec du scan');
    } finally {
      setScanning(false);
    }
  }, [tenant?.id, deliveryId, showToast, patchDraft, adoptProductUnit, adoptProductPrice]);

  /**
   * SCAN DEPUIS LA LISTE — le geste premier de la réception : on prend un colis,
   * on le scanne, et sa ligne du bon s'ouvre. Trois issues :
   *  1. une ligne du bon porte ce code (déjà lu, ou référence fournisseur du
   *     parsing, ou hérité du produit rapproché) → on l'ouvre ;
   *  2. le code n'est sur aucune ligne — le cas courant au premier passage, les
   *     emails de commande ne donnant pas d'EAN → on demande à quelle ligne
   *     l'associer, et l'association vaut pour les fois suivantes ;
   *  3. la ligne portant ce code est déjà traitée → on le dit plutôt que de
   *     rouvrir en silence.
   */
  const scanToSelectLine = useCallback(async (barcode: string) => {
    if (!tenant?.id || !deliveryId) return;
    if (scanning) {
      showToast('Un scan est en cours — recommencez dans un instant');
      return;
    }
    const code = barcode.trim();
    if (!code) return;

    // Correspondance locale d'abord : évite un aller-retour, et retrouve aussi
    // les lignes déjà traitées (que l'endpoint de réception exclut).
    // Deux règles de priorité : les lignes ENCORE À TRAITER d'abord — un même
    // produit peut occuper deux lignes (deux DLC, deux lots), et sans cela la
    // seconde serait injoignable au scan une fois la première approuvée ; puis
    // le code porté par la ligne avant celui déduit du rapprochement par nom,
    // qui est faillible.
    const matches = (l: DeliveryLine, strict: boolean) => (
      strict
        ? l.barcode === code || l.product_code === code
        : l.matched_product_barcode === code
    );
    const known =
      linesRef.current.find((l) => l.status === 'pending' && matches(l, true))
      ?? linesRef.current.find((l) => l.status === 'pending' && matches(l, false))
      ?? linesRef.current.find((l) => matches(l, true) || matches(l, false));
    if (known) {
      if (known.status !== 'pending') {
        const etat = known.status === 'rejected' ? 'refusée' : 'traitée';
        showToast(`« ${known.raw_name} » : ligne déjà ${etat}`);
        return;
      }
      setActiveLineId(known.id);
      showToast(`Ligne ouverte : ${known.raw_name}`);
      return;
    }

    setScanning(true);
    try {
      const res = await api.receiveLineByBarcode(deliveryId, code);
      const fresh = await api.getDeliveryLines(tenant.id, deliveryId);
      setLines(fresh);
      setActiveLineId(res.id);
      showToast(`Ligne ouverte : ${res.raw_name}`);
    } catch {
      // Aucune ligne en attente ne porte ce code : laisser l'opérateur désigner
      // la bonne, puis mémoriser l'association sur la ligne.
      setOrphanBarcode(code);
    } finally {
      setScanning(false);
    }
  }, [tenant?.id, deliveryId, scanning, showToast]);

  /**
   * Scan reçu alors qu'une ligne est ouverte. Deux intentions possibles, et les
   * confondre coûte cher : corriger le rattachement de CETTE ligne, ou passer à
   * l'article suivant en croyant être resté sur la liste. Dans ce second cas, le
   * traitement silencieux collait le code du colis B sur la ligne A et la reliait
   * au produit B — la quantité de A partait alors en stock sur le produit B.
   * Quand la ligne porte déjà un autre code, on demande donc laquelle des deux.
   */
  const applyScan = useCallback(async (barcode: string) => {
    if (!currentLineId || !currentLine) return;
    if (scanning) {
      showToast('Un scan est en cours — recommencez dans un instant');
      return;
    }
    const code = barcode.trim();
    if (!code) return;

    const existing = currentLine.barcode || currentLine.matched_product_barcode;
    if (existing && existing !== code) {
      setConfirm({
        title: 'Autre code-barres',
        message: `Cette ligne (« ${currentLine.raw_name} ») porte déjà le code ${existing}.\n\n` +
          `Vous venez de scanner ${code}.`,
        secondaryLabel: 'C’est un autre article — ouvrir sa ligne',
        onSecondary: () => { setConfirm(null); setActiveLineId(null); void scanToSelectLine(code); },
        confirmLabel: 'Corriger le code de cette ligne',
        onConfirm: () => { setConfirm(null); void applyScanToLine(currentLineId, code); },
      });
      return;
    }
    await applyScanToLine(currentLineId, code);
  }, [currentLineId, currentLine, scanning, applyScanToLine, scanToSelectLine, showToast]);

  /** Associe un code scanné « orphelin » à la ligne choisie, puis l'ouvre. */
  const attachOrphanToLine = async (line: DeliveryLine) => {
    if (!tenant?.id || !deliveryId || !orphanBarcode) return;
    const code = orphanBarcode;
    setOrphanBarcode(null);
    setActiveLineId(line.id);
    setDrafts((prev) => (prev[line.id] ? prev : { ...prev, [line.id]: seedDraft(line) }));
    await applyScanToLine(line.id, code);
  };

  // Douchette (Mac mini, ou appareil tactile avec douchette déclarée).
  // Sur la LISTE le scan choisit la ligne ; dans une ligne ouverte il corrige le
  // rattachement au produit du catalogue.
  useBarcodeScan({
    onScan: currentLine ? applyScan : scanToSelectLine,
    enabled: !scannerOpen && !webcamOpen && !rejectOpen && !packSizeOpen && !confirm && !orphanBarcode,
    wedgeOnTouch,
  });

  const toggleWedge = () => {
    const next = !wedgeOnTouch;
    try {
      if (next) localStorage.setItem(WEDGE_SCANNER_STORAGE_KEY, '1');
      else localStorage.removeItem(WEDGE_SCANNER_STORAGE_KEY);
    } catch {
      // Mode privé : le réglage ne survivra pas à la session, mais il agit tout de suite.
    }
    setWedgeOnTouch(next);
    showToast(next ? 'Douchette activée sur cet appareil' : 'Douchette désactivée');
  };

  // ── Étape 2 : rapprochement ───────────────────────────────────────────────
  const selectProduct = async (product: ProductSuggestion | null) => {
    if (!tenant?.id || !deliveryId || !currentLineId || busy) return;
    const lineId = currentLineId;
    setBusy(true);
    try {
      const previousBarcode = currentLine?.barcode;
      const inheritedBarcode = !!previousBarcode && previousBarcode === currentLine?.matched_product_barcode;
      await api.selectProductForLine(lineId, product?.id ?? null);
      // Un EAN hérité du produit qu'on vient de détacher créerait un doublon de
      // code-barres sur le nouveau produit : on l'efface avant de relire la ligne.
      if (product === null && inheritedBarcode) {
        await api.updateDeliveryLine(tenant.id, lineId, { barcode: '' });
      }
      const fresh = await api.getDeliveryLines(tenant.id, deliveryId);
      setLines(fresh);
      setScanCandidates((prev) => ({ ...prev, [lineId]: [] }));
      patchDraft(lineId, { markedAsNew: product === null });
      const matched = product ? fresh.find((l) => l.id === lineId) : null;
      if (matched) {
        adoptProductUnit(lineId, matched.matched_product_unit, matched.matched_product_is_weighted);
        adoptProductPrice(lineId, matched.matched_product_price_cents);
      }
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Échec du rapprochement');
    } finally {
      setBusy(false);
    }
  };

  // ── Étape 3 : photo étiquette + lecture DLC/lot ───────────────────────────
  // L'OCR met jusqu'à 12 s (repli cloud) et la caméra se referme dès la prise de
  // vue : l'opérateur a largement le temps de passer à la ligne suivante. Tout
  // ce qui suit vise `lineId`, jamais « la ligne affichée ».
  const handlePhoto = async (file: File) => {
    if (!currentLineId) return;
    setWebcamOpen(false);
    if (uploadingPhotoLineId) {
      showToast('Photo précédente encore en cours d’envoi');
      return;
    }
    const lineId = currentLineId;
    setUploadingPhotoLineId(lineId);
    try {
      const toUpload = await prepareImageForUpload(file);
      const res = await api.uploadLineLabelPhoto(lineId, toUpload);
      setLines((prev) => prev.map((l) =>
        l.id === lineId ? { ...l, label_photo_url: res.label_photo_url } : l));
      setUploadingPhotoLineId(null);
      setExtractingLineId(lineId);
      const info = await readLabel(
        toUpload,
        extractLabelLocal,
        (b64, mime) => api.extractLabelInfo(b64, mime),
      );
      if (info) {
        // Ne pas écraser une saisie manuelle : la lecture est une aide, pas une vérité.
        setDrafts((prev) => {
          const base = prev[lineId];
          if (!base) return prev;
          return {
            ...prev,
            [lineId]: {
              ...base,
              labelInfo: info,
              dlc: base.dlc || info.dlc || '',
              lotNumber: base.lotNumber || info.lot_number || '',
            },
          };
        });
      }
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Échec de l’envoi de la photo');
    } finally {
      setUploadingPhotoLineId((id) => (id === lineId ? null : id));
      setExtractingLineId((id) => (id === lineId ? null : id));
    }
  };

  // ── Étape 5 : conditionnement colis → kg ──────────────────────────────────
  const applyPackSize = () => {
    if (!currentLineId || !draft) return;
    const n = parseFloat(packSize.replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) {
      showToast('Poids invalide');
      return;
    }
    if (packMode.mode === 'prix') {
      diviserPrixAchat(n);
      setPackSizeOpen(false);
      setPackSize('');
      return;
    }
    const qty = parseFloat(draft.quantity.replace(',', '.')) || 0;
    const newQty = Math.round(qty * n * 1000) / 1000;
    const newPurchase = Math.round(draft.purchaseCents / n);
    patchDraft(currentLineId, {
      quantity: String(newQty),
      purchaseCents: newPurchase,
      saleCents: suggestedSalePriceCents(newPurchase),
      unit: 'kg',
      unitTouched: true,
      prixAchatVerifie: false,
    });
    setPackSizeOpen(false);
    setPackSize('');
    showToast(`${formatQty(newQty)} kg à ${(newPurchase / 100).toFixed(2)} €/kg`);
  };

  /**
   * Le prix d'achat est celui d'un colis de `n` unités, la quantité est juste :
   * on ne divise QUE le prix. Le prix de vente proposé d'un produit existant ne
   * bouge pas (c'est celui de la caisse) ; celui d'un nouveau produit suit l'achat.
   */
  const diviserPrixAchat = (n: number) => {
    if (!currentLineId || !draft || !(n > 0)) return;
    const newPurchase = Math.round(draft.purchaseCents / n);
    const existant = !!currentLine?.matched_product_id && !draft.markedAsNew;
    patchDraft(currentLineId, {
      purchaseCents: newPurchase,
      ...(!existant && !draft.saleEdited ? { saleCents: suggestedSalePriceCents(newPurchase) } : {}),
      prixAchatVerifie: false,
    });
    showToast(`Prix d’achat : ${(newPurchase / 100).toFixed(2)} € par ${draft.unit === 'kg' ? 'kg' : draft.unit}`);
  };

  // ── Étape 6 : approbation ─────────────────────────────────────────────────
  const doApprove = async (opts: { force?: boolean; applySalePrice?: boolean } = {}) => {
    if (!tenant?.id || !deliveryId || !currentLine || !draft) return;
    // approveLine n'est pas idempotent : un second envoi créerait un second lot.
    if (approvingRef.current) return;
    const line = currentLine;
    // Le rechargement des lignes peut échouer APRÈS une approbation réussie (4G au
    // marché) : la ligne resterait affichée « à traiter » et un second appui
    // créerait un deuxième lot. On mémorise ce qui est parti.
    if (approvedIdsRef.current.has(line.id)) {
      showToast('Ligne déjà réceptionnée — rechargez le bon');
      return;
    }
    approvingRef.current = true;
    setBusy(true);
    try {
      const qty = parseFloat(draft.quantity.replace(',', '.'));
      // 1. Persister les champs saisis. approveLine ignore unit_price_ttc_cents
      //    (il attend des euros) ; updateDeliveryLine fait la conversion.
      //    La DLC n'est PAS envoyée ici : updateDeliveryLine passe la valeur brute
      //    à Prisma, qui refuse « 2026-12-11 » (il exige un ISO-8601 complet) et
      //    renvoie un 500. C'est approveLine qui la convertit correctement.
      //    Les champs vidés partent en clair ('' / 0) : ne pas les envoyer
      //    laisserait en base la valeur du parsing que l'opérateur vient d'effacer.
      const fieldUpdates: Partial<DeliveryLine> = {
        unit: draft.unit,
        supplier_lot_number: draft.lotNumber,
      };
      if (Number.isFinite(qty)) fieldUpdates.quantity = qty;
      if (draft.purchaseCents >= 0) fieldUpdates.unit_price_ttc_cents = draft.purchaseCents;
      if (draft.markedAsNew && draft.newName.trim()) fieldUpdates.normalized_name = draft.newName.trim();
      await api.updateDeliveryLine(tenant.id, line.id, fieldUpdates);

      // 2. Approuver → crée/associe le produit, crée le lot en réserve.
      const approveUpdates: any = {};
      if (draft.dlc) approveUpdates.default_expiry_date = draft.dlc;
      if (draft.saleCents > 0) approveUpdates.suggested_price_cents = draft.saleCents;
      // Écraser le prix du rayon touche du stock déjà en place : uniquement sur
      // confirmation explicite (handleApprove), jamais déduit de l'état du formulaire.
      if (opts.applySalePrice) approveUpdates.apply_sale_price = true;
      if (draft.markedAsNew || opts.force) approveUpdates.force_new_product = true;

      const result = await api.approveLine(tenant.id, line.id, approveUpdates);

      if (result?.requires_confirmation && result.reason === 'ean_mismatch') {
        approvingRef.current = false;
        setBusy(false);
        setConfirm({
          title: 'Code-barres différent',
          message: `${result.message}\n\n`
            + 'Annuler pour scanner l’article : si son code est celui de la fiche, la ligne sera corrigée.',
          confirmLabel: 'Créer une nouvelle fiche',
          onConfirm: () => { setConfirm(null); void doApprove({ ...opts, force: true }); },
        });
        return;
      }

      if (result?.requires_confirmation) {
        const names = (result.duplicate_candidates || [])
          .map((c: any) => `• ${c.name}${c.barcode ? ` (EAN ${c.barcode})` : ''}`)
          .join('\n');
        approvingRef.current = false;
        setBusy(false);
        setConfirm({
          title: 'Produit déjà au catalogue ?',
          message: `${result.message}\n\nProduits similaires :\n${names}\n\n` +
            'Annuler pour scanner le bon produit, ou confirmer la création d’un produit distinct.',
          confirmLabel: 'Créer quand même',
          onConfirm: () => { setConfirm(null); void doApprove({ ...opts, force: true }); },
        });
        return;
      }

      // Refus explicite : le stock n'a pas pu être créé et le serveur a remis la
      // ligne à traiter. À placer AVANT le verrou anti-doublon, sinon la ligne
      // serait considérée comme partie et l'opérateur ne pourrait plus réessayer.
      if (result?.success === false) {
        approvingRef.current = false;
        setBusy(false);
        const fresh = await api.getDeliveryLines(tenant.id, deliveryId);
        setLines(fresh);
        setConfirm({
          title: 'Stock non créé',
          message: `${result.error || 'Erreur inconnue'}\n\n`
            + 'La ligne est restée à traiter : recommencez.',
          confirmLabel: 'J’ai compris',
          onConfirm: () => setConfirm(null),
        });
        return;
      }

      // Le serveur a créé le lot : plus aucun second envoi ne doit partir, même si
      // le rechargement ci-dessous échoue.
      approvedIdsRef.current.add(line.id);

      // Colis à deux dates limites : le lot vient d'être créé avec la première,
      // on en détache aussitôt un jumeau pour la seconde. L'échec ne remet pas
      // la réception en cause — le stock est entré, il manque juste la scission.
      // La validation de handleApprove garantit ici une quantité et une date
      // exploitables : plus de sortie silencieuse.
      let splitDone = false;
      let splitError: string | null = null;
      if (draft.secondLot && result?.lotId) {
        const splitQty = parseFloat(draft.secondLot.quantity.replace(',', '.'));
        try {
          // Via ApiClient et non posApi : ce dernier fait primer un éventuel
          // jeton client sur la session staff (401 silencieux).
          await api.post(`/lots/${result.lotId}/split`, {
            quantity_backroom: splitQty,
            expiry_date: draft.secondLot.dlc,
            supplier_lot_number: draft.secondLot.lotNumber || draft.lotNumber || undefined,
          });
          splitDone = true;
        } catch (err: any) {
          splitError = err?.response?.data?.message || err?.message || 'Erreur inconnue';
        }
      }

      const fresh = await api.getDeliveryLines(tenant.id, deliveryId);
      setLines(fresh);
      // Retour à la liste : le colis suivant se choisit au scan, pas dans l'ordre du bon.
      setActiveLineId(null);
      setDrafts((prev) => {
        const { [line.id]: _done, ...rest } = prev;
        return rest;
      });

      // Première arrivée ou retour après une longue absence : le demander
      // MAINTENANT, l'article en main — c'était oublié une fois rangé.
      await invitationNouveaute.proposer(result);

      if (result?.warning) {
        setConfirm({
          title: 'Ligne approuvée, stock non créé',
          message: `${result.warning}\n\nVérifiez la quantité et le stock de ce produit avant la mise en rayon.`,
          confirmLabel: 'J’ai compris',
          onConfirm: () => setConfirm(null),
        });
        return;
      }
      if (splitError) {
        setConfirm({
          title: 'Scission impossible',
          message: `${splitError}\n\nLe stock est bien entré en un seul lot : la scission ` +
            'reste possible depuis la mise en rayon.',
          confirmLabel: 'J’ai compris',
          onConfirm: () => setConfirm(null),
        });
        return;
      }
      // Prix changé : c'est l'étiquette du rayon qu'on oublie, et le client la
      // voit avant nous. On le dit une bonne fois, en bloquant, plutôt qu'en
      // message fugace.
      if (opts.applySalePrice) {
        setConfirm({
          title: 'Prix changé en caisse',
          message: `« ${line.matched_product_name || line.raw_name} » est désormais à `
            + `${(draft.saleCents / 100).toFixed(2)} €.\n\n`
            + 'Changez le prix du rayonnage en magasin.',
          confirmLabel: 'C’est noté',
          onConfirm: () => setConfirm(null),
        });
        return;
      }
      // Un seul message final : dire « réceptionné » puis l'écraser aussitôt
      // laisserait croire que la scission n'a pas eu lieu.
      showToast(splitDone ? '✓ Réceptionné en 2 lots' : '✓ Ligne réceptionnée');
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Échec de la réception');
    } finally {
      approvingRef.current = false;
      setBusy(false);
    }
  };

  const handleApprove = (opts?: { prixVerifie?: boolean }) => {
    if (!currentLine || !draft) return;

    // Une quantité illisible serait ignorée en silence et la ligne partirait avec
    // la quantité du parsing IA — précisément celle qu'on vient de corriger.
    const qty = parseFloat(draft.quantity.replace(',', '.'));
    if (!Number.isFinite(qty) || qty <= 0) {
      showToast('Indiquez une quantité reçue valide');
      return;
    }

    // Prix d'achat suspect : on demande AVANT de créer le lot. Après, le prix faux
    // vit dans la marge, la valeur du stock et la rentabilité sans que personne
    // ne le revoie (9 lots Vajra entrés au prix du sac, 30/09 → 04/11/2026).
    // `opts?.prixVerifie === true` : le clic du bouton passe un événement, qui ne
    // doit pas valoir confirmation.
    if (alerteAchat && opts?.prixVerifie !== true) {
      const achat = `${(draft.purchaseCents / 100).toFixed(2)} €`;
      const unite = draft.unit === 'kg' ? 'le kilo' : draft.unit === 'L' ? 'le litre' : 'la pièce';
      const repere = `${(alerteAchat.repereCents / 100).toFixed(2)} €`;
      const k = alerteAchat.diviseur;
      setConfirm({
        title: 'Prix d’achat du colis ?',
        message: (alerteAchat.motif === 'habituel'
          ? `Prix d’achat : ${achat} ${unite}, soit ${formatQty(Math.round(alerteAchat.rapport * 10) / 10)} × le prix habituel (${repere}).`
          : `Prix d’achat : ${achat} ${unite}, alors qu’il se vend ${repere} en caisse.`)
          + '\n\nC’est souvent le prix d’un sac ou d’un carton entier, lu comme un prix unitaire.',
        confirmLabel: k
          ? `Diviser par ${formatQty(k)} → ${(Math.round(draft.purchaseCents / k) / 100).toFixed(2)} €`
          : 'Corriger le prix',
        onConfirm: () => {
          setConfirm(null);
          if (k) {
            diviserPrixAchat(k);
          } else {
            setPackMode({ mode: 'prix' });
            setPackSizeOpen(true);
          }
        },
        secondaryLabel: 'Le prix est juste',
        onSecondary: () => {
          setConfirm(null);
          patchDraft(currentLine.id, { prixAchatVerifie: true });
          handleApprove({ prixVerifie: true });
        },
      });
      return;
    }

    // Seconde date déclarée mais incomplète : le stock entrerait en UN lot sans
    // rien dire, et l'opérateur croirait en avoir deux. Bloquer avant, pas après
    // — une fois le lot créé, la scission se rattrape ailleurs.
    if (draft.secondLot) {
      const splitQty = parseFloat(draft.secondLot.quantity.replace(',', '.'));
      if (!Number.isFinite(splitQty) || splitQty <= 0) {
        showToast('Indiquez la quantité du second lot');
        return;
      }
      if (splitQty >= qty) {
        showToast(`Le second lot doit être inférieur aux ${formatQty(qty)} reçus`);
        return;
      }
      if (!draft.secondLot.dlc) {
        showToast('Indiquez la seconde date limite (JJ/MM/AAAA)');
        return;
      }
    }

    const isExisting = !!currentLine.matched_product_id && !draft.markedAsNew;

    // Prix de vente modifié sur un produit existant : il s'appliquera au stock
    // déjà en rayon. On demande AUSSI quand le prix actuel est inconnu (produit
    // rattaché par scan, donc absent des suggestions par nom) — ne pas demander
    // reviendrait à écraser un prix sans que personne l'ait vu.
    const askPrice = (next: (applySalePrice: boolean) => void) => {
      // Un champ vidé (0 €) n'est pas un prix : le backend retomberait sur sa
      // marge par défaut, donc annoncer « appliquer 0,00 € » serait un mensonge.
      if (!isExisting || draft.saleCents <= 0) return next(false);
      // Prix porté par la ligne (table `prices`), pas celui des suggestions :
      // un produit rattaché par scan n'y figure souvent pas, et la question ne
      // se posait alors jamais alors que le prix allait bel et bien changer.
      const currentPrice = currentLine.matched_product_price_cents
        ?? (suggestions[currentLine.id] || [])
          .find((s) => s.id === currentLine.matched_product_id)?.price_cents;
      if (currentPrice != null && currentPrice === draft.saleCents) return next(false);
      // On demande dès que le prix proposé diffère de la caisse, RETAPÉ OU NON :
      // un prix d'achat modifié recalcule le prix proposé tout seul, et l'écran
      // annonçait alors « le prix passera à … » sans que rien ne s'applique
      // (Farine Froment 80 %, lots à 6,00 et caisse restée à 5,80 — 28/10/2026).
      // Prix actuel illisible et proposition non retouchée : rien à trancher.
      if (currentPrice == null && !draft.saleEdited) return next(false);
      const propose = `${(draft.saleCents / 100).toFixed(2)} €`;
      const caisse = currentPrice != null ? `${(currentPrice / 100).toFixed(2)} €` : null;
      setConfirm({
        title: 'Quel prix en caisse ?',
        message: (caisse
          ? `En caisse aujourd’hui : ${caisse}\n`
          : 'Le prix actuel de ce produit n’a pas pu être lu.\n') +
          `Proposé : ${propose}` +
          (draft.saleEdited ? '' : ` (calculé sur l’achat à ${(draft.purchaseCents / 100).toFixed(2)} €)`) +
          `\n\nPasser à ${propose} change aussi le prix du stock déjà en rayon : ` +
          'pensez alors à l’étiquette du rayonnage.',
        confirmLabel: `Passer à ${propose}`,
        // Sans cette troisième voie, un prix touché par erreur sur un produit
        // rattaché par scan enfermerait l'opérateur : « Annuler » ne réceptionne
        // pas, et le prix actuel étant inconnu la question revient à chaque essai.
        secondaryLabel: caisse ? `Garder ${caisse}` : 'Recevoir sans changer le prix',
        onSecondary: () => { setConfirm(null); next(false); },
        onConfirm: () => { setConfirm(null); next(true); },
      });
    };

    // Garde-fou nature : un produit au poids ne doit pas recevoir un lot à la pièce.
    if (isExisting && currentLine.matched_product_unit != null) {
      const chosenWeighted = draft.unit.toLowerCase() === 'kg';
      const prodWeighted = currentLine.matched_product_is_weighted === true
        || (currentLine.matched_product_unit || '').toLowerCase() === 'kg';
      if (chosenWeighted !== prodWeighted) {
        setConfirm({
          title: 'Changement de nature',
          message: `« ${currentLine.matched_product_name} » est enregistré ` +
            `${prodWeighted ? 'AU POIDS (kg)' : 'À LA PIÈCE'} sur sa fiche, ` +
            `et vous le réceptionnez ${chosenWeighted ? 'AU POIDS (kg)' : 'À LA PIÈCE'}.\n\n` +
            'Confirmer ce changement ?',
          confirmLabel: 'Confirmer',
          // Les deux questions s'enchaînent : confirmer la nature ne doit pas
          // faire sauter celle du prix.
          onConfirm: () => {
            setConfirm(null);
            askPrice((applySalePrice) => void doApprove({ applySalePrice }));
          },
        });
        return;
      }
    }

    askPrice((applySalePrice) => void doApprove({ applySalePrice }));
  };

  /** Marche arrière sur une ligne déjà réceptionnée (erreur de saisie). */
  const undoLine = async (line: DeliveryLine) => {
    if (!tenant?.id || !deliveryId || approvingRef.current) return;
    approvingRef.current = true;
    setBusy(true);
    try {
      const res = await api.undoLineApproval(tenant.id, line.id);
      const fresh = await api.getDeliveryLines(tenant.id, deliveryId);
      setLines(fresh);
      // La ligne peut repartir : elle n'est plus « déjà envoyée ».
      approvedIdsRef.current.delete(line.id);
      const details = [
        res.deleted_lot_number ? `lot ${res.deleted_lot_number}` : null,
        res.deleted_product_name ? `fiche « ${res.deleted_product_name} »` : null,
      ].filter(Boolean);
      showToast(details.length
        ? `Réception annulée — ${details.join(' et ')} supprimé${details.length > 1 ? 's' : ''}`
        : 'Réception annulée');
    } catch (err: any) {
      // Refus attendu quand le lot a déjà vécu : la raison est actionnable.
      setConfirm({
        title: 'Annulation impossible',
        message: err?.response?.data?.message || 'Erreur inconnue',
        confirmLabel: 'J’ai compris',
        onConfirm: () => setConfirm(null),
      });
    } finally {
      approvingRef.current = false;
      setBusy(false);
    }
  };

  const askUndo = (line: DeliveryLine) => {
    // Une ligne refusée n'a pas de lot : annoncer sa suppression serait faux.
    const rejected = line.status === 'rejected';
    setConfirm({
      title: rejected ? 'Remettre cette ligne à traiter ?' : 'Annuler cette réception ?',
      message: `« ${line.raw_name} »\n\n` + (rejected
        ? 'La ligne redeviendra à traiter.'
        : 'Le lot créé sera supprimé et la ligne redeviendra à traiter. ' +
          'Le stock n’aura jamais existé.'),
      confirmLabel: rejected ? 'Remettre à traiter' : 'Annuler la réception',
      onConfirm: () => { setConfirm(null); void undoLine(line); },
    });
  };

  const handleReject = async (reason: string) => {
    if (!tenant?.id || !deliveryId || !currentLine || approvingRef.current) return;
    if (!reason.trim()) return;
    const line = currentLine;
    approvingRef.current = true;
    setBusy(true);
    setRejectOpen(false);
    setOtherReason('');
    try {
      await api.rejectLine(tenant.id, line.id, reason);
      const fresh = await api.getDeliveryLines(tenant.id, deliveryId);
      setLines(fresh);
      setActiveLineId(null);
      setDrafts((prev) => {
        const { [line.id]: _done, ...rest } = prev;
        return rest;
      });
      showToast('Ligne refusée');
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Échec du refus');
    } finally {
      approvingRef.current = false;
      setBusy(false);
    }
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="min-h-screen bg-[#faf7f0] flex items-center justify-center text-gray-400">Chargement…</div>;
  }
  if (loadError) {
    return (
      <div className="min-h-screen bg-[#faf7f0] flex flex-col items-center justify-center gap-3 p-6">
        <p className="text-red-600">{loadError}</p>
        <Link to="/reception" className="text-[#2d6a4f] font-semibold underline">Retour aux bons</Link>
      </div>
    );
  }

  const allDone = pendingLines.length === 0;

  return (
    <div className="min-h-screen bg-[#faf7f0] pb-[max(7rem,calc(env(safe-area-inset-bottom)+6rem))]">
      {/* En-tête : fournisseur + progression */}
      <header className="sticky top-0 z-20 bg-[#faf7f0]/95 backdrop-blur border-b border-black/[.06]
        px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-3">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Link
            to="/reception"
            className="shrink-0 w-12 h-12 flex items-center justify-center rounded-full bg-white border border-black/[.08]
              shadow-sm active:scale-95 transition-transform select-none touch-manipulation"
            aria-label="Retour aux bons"
          >
            ←
          </Link>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-gray-900 truncate">{delivery?.supplier || 'Réception'}</p>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                <div
                  className="h-full bg-[#2d6a4f] transition-all"
                  style={{ width: lines.length ? `${(doneCount / lines.length) * 100}%` : '0%' }}
                />
              </div>
              <span className="text-xs text-gray-600 tabular-nums shrink-0">{doneCount}/{lines.length}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="px-4 pt-4 max-w-2xl mx-auto space-y-4">
        {allDone ? (
          <div className="text-center py-16">
            <p className="text-5xl mb-3" aria-hidden>✅</p>
            <h2 className="text-xl font-bold text-gray-900">Bon terminé</h2>
            <p className="text-gray-500 mt-1">{doneCount} ligne{doneCount > 1 ? 's' : ''} traitée{doneCount > 1 ? 's' : ''}.</p>
            <div className="mt-6 space-y-3">
              <button
                onClick={() => navigate('/pos/reception?tab=rayon')}
                className="w-full h-14 rounded-2xl bg-[#2d6a4f] text-white font-semibold shadow-md
                  active:scale-[.98] transition-transform select-none touch-manipulation"
              >
                ⬆️ Passer à la mise en rayon
              </button>
              <button
                onClick={() => navigate('/reception')}
                className="w-full h-12 rounded-2xl bg-white border border-black/[.08] text-gray-700 font-semibold
                  active:scale-[.98] transition-transform select-none touch-manipulation"
              >
                Retour aux bons
              </button>
              <button
                onClick={() => setArticleEnPlusOuvert(true)}
                className="w-full h-12 rounded-2xl border border-emerald-600 bg-emerald-50 text-emerald-800 font-semibold
                  active:scale-[.98] transition-transform select-none touch-manipulation"
              >
                + Article reçu en plus (absent du bon)
              </button>
            </div>
            {articleEnPlusOuvert && deliveryId && (
              <ArticleEnPlusModal
                deliveryId={deliveryId}
                onClose={() => setArticleEnPlusOuvert(false)}
                onAjoute={() => loadAll()}
              />
            )}
          </div>
        ) : !currentLine ? (
          /* ── ÉCRAN 1 : les lignes du bon. Le scan en choisit une. ────────── */
          <>
            <section className="bg-white rounded-2xl border border-black/[.08] shadow-sm p-4">
              <p className="font-bold text-gray-900">
                {scanning ? 'Lecture du code…' : 'Scannez un article'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Sa ligne du bon s’ouvre automatiquement. Sinon, appuyez sur la ligne
                dans la liste ci-dessous.
              </p>
              <button
                onClick={() => setScannerOpen(true)}
                disabled={scanning}
                className="mt-3 w-full h-12 rounded-xl bg-gray-900 text-white font-semibold
                  disabled:opacity-50 active:scale-[.98] transition-transform select-none touch-manipulation"
              >
                📷 Scanner avec la caméra
              </button>
              {IS_TOUCH_DEVICE && (
                <button
                  onClick={toggleWedge}
                  className={`mt-2 w-full min-h-[48px] px-3 rounded-xl border text-sm font-semibold
                    active:scale-[.98] transition-transform select-none touch-manipulation
                    ${wedgeOnTouch
                      ? 'bg-[#2d6a4f]/10 border-[#2d6a4f] text-[#2d6a4f]'
                      : 'bg-white border-gray-300 text-gray-600'}`}
                >
                  {wedgeOnTouch
                    ? '✓ Douchette branchée — scan direct actif'
                    : '⌨️ J’ai branché une douchette sur cet appareil'}
                </button>
              )}
              {/* Un article livré qui n'est pas sur le bon importé (reliquat d'une
                  autre commande, ajout du fournisseur) : sans ceci, impossible
                  de le réceptionner — cas Hygiena du 14/10/2026. */}
              <button
                onClick={() => setArticleEnPlusOuvert(true)}
                className="mt-2 w-full min-h-[48px] px-3 rounded-xl border border-emerald-600 bg-emerald-50 text-emerald-800
                  text-sm font-semibold active:scale-[.98] transition-transform select-none touch-manipulation"
              >
                + Article reçu en plus (absent du bon)
              </button>
            </section>
            {articleEnPlusOuvert && deliveryId && (
              <ArticleEnPlusModal
                deliveryId={deliveryId}
                onClose={() => setArticleEnPlusOuvert(false)}
                onAjoute={() => loadAll()}
              />
            )}

            <section className="space-y-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 px-1">
                {pendingLines.length} ligne{pendingLines.length > 1 ? 's' : ''} a traiter
              </h2>
              {/* Sur un bon de 74 lignes, faire defiler les 23 deja rentrees pour
                  atteindre la suivante coute du temps a chaque article : les
                  lignes traitees passent en bas, repliees. */}
              {pendingLines.map((l) => {
                const treated = l.status !== 'pending';
                return (
                  <div
                    key={l.id}
                    className={`rounded-2xl border shadow-sm
                      ${treated ? 'bg-gray-50 border-black/[.04]' : 'bg-white border-black/[.08]'}`}
                  >
                  <button
                    onClick={() => setActiveLineId(l.id)}
                    disabled={treated}
                    className={`w-full text-left p-3 flex items-center gap-3
                      transition-transform duration-100 select-none touch-manipulation
                      ${treated ? 'opacity-60' : 'active:scale-[.98]'}`}
                  >
                    <span className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-sm font-bold
                      ${l.status === 'approved' ? 'bg-[#2d6a4f] text-white'
                        : l.status === 'rejected' ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-500'}`}>
                      {l.status === 'approved' ? '✓' : l.status === 'rejected' ? '✕' : '·'}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-gray-900 truncate">{l.raw_name}</span>
                      <span className="block text-xs text-gray-500 truncate">
                        {l.ordered_quantity != null && `${formatQty(l.ordered_quantity)} ${l.unit || ''}`}
                        {l.ordered_quantity == null && l.quantity != null && `${formatQty(l.quantity)} ${l.unit || ''}`}
                        {l.barcode ? ` · ${l.barcode}` : ''}
                      </span>
                      {/* Motif du refus : une consigne écartée et un produit
                          abîmé finissent tous deux « refusés », alors que l'un
                          est une ligne de facture normale et l'autre le point de
                          départ d'une réclamation. Le motif est ce qui les
                          distingue — autant le montrer. */}
                      {l.status === 'rejected' ? (
                        <span className="block text-xs text-red-700 truncate">
                          ✕ {l.notes || 'Refusée'}
                        </span>
                      ) : l.matched_product_id ? (
                        <span className="block text-xs text-[#2d6a4f] truncate">
                          → {l.matched_product_name}
                        </span>
                      ) : !treated && (
                        <span className="block text-xs text-amber-700 truncate">
                          → aucun produit rapproché
                        </span>
                      )}
                    </span>
                    {!treated && <span className="shrink-0 text-gray-300" aria-hidden>›</span>}
                  </button>
                  {/* Marche arrière : une erreur de saisie (mauvais code-barres,
                      mauvaise quantité) ne doit pas exiger du SQL. Le serveur
                      refuse si le lot a déjà vécu. */}
                  {treated && (
                    <button
                      onClick={() => askUndo(l)}
                      disabled={busy}
                      className="w-full min-h-[48px] px-3 pb-2 text-left text-xs text-[#2d6a4f] font-semibold
                        disabled:opacity-40 select-none touch-manipulation"
                    >
                      {l.status === 'rejected' ? '↩︎ Remettre à traiter' : '↩︎ Annuler cette réception'}
                    </button>
                  )}
                  </div>
                );
              })}
            </section>

            {/* Lignes deja traitees : repliees, mais accessibles — c'est de la
                que se corrige une erreur (annuler une reception). */}
            {doneCount > 0 && (
              <section>
                <button
                  onClick={() => setShowDone((v) => !v)}
                  className="w-full min-h-[48px] text-sm font-semibold text-gray-500 text-left px-1
                    select-none touch-manipulation"
                >
                  {showDone ? '▾' : '▸'} {doneCount} ligne{doneCount > 1 ? 's' : ''} traitee{doneCount > 1 ? 's' : ''}
                </button>
                {showDone && (
                  <div className="space-y-2 mt-2">
                    {lines.filter((l) => l.status !== 'pending').map((l) => (
                      <div key={l.id} className="rounded-2xl border border-black/[.04] bg-gray-50 p-3">
                        <div className="flex items-center gap-3">
                          <span className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-sm font-bold
                            ${l.status === 'approved' ? 'bg-[#2d6a4f] text-white' : 'bg-red-100 text-red-700'}`}>
                            {l.status === 'approved' ? '\u2713' : '\u2715'}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-semibold text-gray-700 truncate text-sm">{l.raw_name}</span>
                            {l.status === 'rejected' && (
                              <span className="block text-xs text-red-700 truncate">{l.notes || 'Refusee'}</span>
                            )}
                          </span>
                        </div>
                        <button
                          onClick={() => askUndo(l)}
                          disabled={busy}
                          className="w-full min-h-[48px] mt-1 text-left text-xs text-[#2d6a4f] font-semibold
                            disabled:opacity-40 select-none touch-manipulation"
                        >
                          {l.status === 'rejected' ? '\u21A9\uFE0E Remettre a traiter' : '\u21A9\uFE0E Annuler cette reception'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}
          </>
        ) : draft && (
          <>
            {/* ── ÉCRAN 2 : traitement de la ligne choisie ─────────────────── */}
            <button
              onClick={() => setActiveLineId(null)}
              className="min-h-[48px] text-sm text-[#2d6a4f] font-semibold select-none touch-manipulation"
            >
              ← Toutes les lignes
            </button>

            {/* Ligne du bon */}
            <section className="bg-white rounded-2xl border border-black/[.08] shadow-sm p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-gray-400">Ligne du bon</p>
              <h2 className="text-lg font-bold text-gray-900 leading-tight mt-1">{currentLine.raw_name}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {currentLine.ordered_quantity != null && `Commandé : ${formatQty(currentLine.ordered_quantity)} ${currentLine.unit || ''} · `}
                {currentLine.unit_price_ttc_cents != null && `${(currentLine.unit_price_ttc_cents / 100).toFixed(2)} € l’unité`}
              </p>
            </section>

            {/* 1 — Scan (le chemin rapide, pas un passage obligé : le vrac et
                les fruits et légumes n'ont pas de code-barres) */}
            <Step n={1} title="Scanner le code-barres" done={!!currentLine.barcode} optional>
              {currentLine.barcode ? (
                <p className="text-sm text-gray-600">
                  Code lu : <span className="font-mono font-semibold text-gray-900">{currentLine.barcode}</span>
                </p>
              ) : (
                <p className="text-sm text-gray-500">
                  Douchette : scannez directement. Sinon, utilisez la caméra —
                  ou passez à l’étape 2 pour choisir le produit à la main.
                </p>
              )}
              <button
                onClick={() => setScannerOpen(true)}
                disabled={scanning}
                className="mt-3 w-full h-12 rounded-xl bg-gray-900 text-white font-semibold
                  disabled:opacity-50 active:scale-[.98] transition-transform select-none touch-manipulation"
              >
                {scanning ? 'Lecture…' : '📷 Scanner avec la caméra'}
              </button>
              {/* Sur tout appareil tactile — iPhone comme iPad —, une douchette
                  USB/Bluetooth tape comme un clavier, mais le hook de scan s'y
                  tait par défaut : le clavier virtuel iOS produit des salves qu'il
                  prendrait pour des scans. L'interrupteur était jusqu'ici un
                  réglage de console, donc hors de portée sur ces appareils. */}
              {IS_TOUCH_DEVICE && (
                <button
                  onClick={toggleWedge}
                  className={`mt-2 w-full min-h-[48px] px-3 rounded-xl border text-sm font-semibold
                    active:scale-[.98] transition-transform select-none touch-manipulation
                    ${wedgeOnTouch
                      ? 'bg-[#2d6a4f]/10 border-[#2d6a4f] text-[#2d6a4f]'
                      : 'bg-white border-gray-300 text-gray-600'}`}
                >
                  {wedgeOnTouch
                    ? '✓ Douchette branchée — scan direct actif'
                    : '⌨️ J’ai branché une douchette sur cet appareil'}
                </button>
              )}
            </Step>

            {/* 2 — Rapprochement */}
            <Step
              n={2}
              title="Produit du catalogue"
              done={!!currentLine.matched_product_id || (draft.markedAsNew && !!draft.newName.trim())}
            >
              {currentLine.matched_product_id && !draft.markedAsNew ? (
                <div className="flex items-center gap-3">
                  {currentLine.matched_product_image_url && (
                    <img
                      src={currentLine.matched_product_image_url}
                      alt=""
                      className="w-14 h-14 rounded-xl object-cover border border-black/[.06]"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900 truncate">{currentLine.matched_product_name}</p>
                    <p className="text-xs text-gray-500 truncate">{currentLine.matched_category_name}</p>
                    {/* L'analyse du bon propose un rapprochement ; seul le scan de
                        l'article le confirme par la réalité physique. Distinguer les
                        deux évite d'approuver une proposition sans l'avoir vérifiée. */}
                    {/* Le code-barres fait foi : un code DIFFÉRENT de celui de la
                        fiche n'est pas une confirmation, c'est un autre produit
                        (Clearly Organic, 29/10 : la facture portait l'EAN du bon
                        vin, l'écran disait « confirmé » sur le mauvais). */}
                    {eanConflit(currentLine.barcode, currentLine.matched_product_barcode) ? (
                      <p className="text-sm text-red-700 font-semibold">
                        ⚠️ Code-barres différent : {currentLine.barcode} ≠ {currentLine.matched_product_barcode} sur
                        la fiche. C’est un autre produit — scannez l’article, ou « Changer » pour créer sa fiche.
                      </p>
                    ) : currentLine.barcode ? (
                      <p className="text-xs text-[#2d6a4f] font-semibold">✓ confirmé par le code-barres</p>
                    ) : (
                      <p className="text-xs text-amber-700">
                        proposé par l’analyse — scannez l’article pour confirmer
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => selectProduct(null)}
                    disabled={busy}
                    className="shrink-0 min-h-[48px] px-2 text-sm text-[#2d6a4f] font-semibold underline underline-offset-2
                      disabled:opacity-40 select-none touch-manipulation"
                  >
                    Changer
                  </button>
                </div>
              ) : draft.markedAsNew ? (
                <div>
                  <p className="text-sm font-semibold text-amber-700 mb-2">➕ Nouveau produit</p>
                  <ProductAutocomplete
                    value={draft.newName}
                    onChange={(v) => patchDraft(currentLine.id, { newName: v })}
                    onProductSelect={(p) => { patchDraft(currentLine.id, { newName: p.name }); void selectProduct(p); }}
                    placeholder="Nom du nouveau produit…"
                  />
                  <button
                    onClick={() => patchDraft(currentLine.id, { markedAsNew: false })}
                    className="mt-2 min-h-[48px] text-sm text-gray-500 underline underline-offset-2
                      select-none touch-manipulation"
                  >
                    Annuler — c’est un produit existant
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {(scanCandidates[currentLine.id]?.length
                    ? scanCandidates[currentLine.id]
                    : suggestions[currentLine.id] || []
                  ).slice(0, 4).map((p) => (
                    <button
                      key={p.id}
                      onClick={() => selectProduct(p)}
                      disabled={busy}
                      className="w-full flex items-center gap-3 p-2 rounded-xl border border-black/[.08] bg-gray-50
                        disabled:opacity-40 active:scale-[.98] transition-transform text-left select-none touch-manipulation"
                    >
                      {p.image_url
                        ? <img src={p.image_url} alt="" className="w-11 h-11 rounded-lg object-cover shrink-0" />
                        : <span className="w-11 h-11 rounded-lg bg-gray-200 shrink-0" aria-hidden />}
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold text-gray-900 truncate text-sm">{p.name}</span>
                        {p.price_cents != null && (
                          <span className="block text-xs text-gray-500">{(p.price_cents / 100).toFixed(2)} €</span>
                        )}
                      </span>
                    </button>
                  ))}
                  {/* Recherche libre : tout ne se scanne pas (vrac, fruits et
                      légumes, coupe). Sans elle, un produit absent des trois
                      suggestions n'aurait d'autre issue que « Nouveau produit »,
                      donc un doublon. */}
                  <div className="pt-1">
                    <label className="block text-xs font-semibold text-gray-500 mb-1">
                      Pas de code-barres ? Cherchez le produit
                    </label>
                    <ProductAutocomplete
                      value={draft.searchName}
                      onChange={(v) => patchDraft(currentLine.id, { searchName: v })}
                      onProductSelect={(p) => {
                        patchDraft(currentLine.id, { searchName: '' });
                        void selectProduct(p);
                      }}
                      placeholder="Nom du produit au catalogue…"
                    />
                  </div>
                  <button
                    onClick={() => patchDraft(currentLine.id, {
                      markedAsNew: true,
                      newName: currentLine.normalized_name || currentLine.raw_name,
                    })}
                    className="w-full h-12 rounded-xl border-2 border-dashed border-gray-300 text-gray-600 font-semibold
                      active:scale-[.98] transition-transform select-none touch-manipulation"
                  >
                    ➕ Nouveau produit
                  </button>
                </div>
              )}
            </Step>

            {/* 3 — Photo étiquette */}
            <Step n={3} title="Photo étiquette (DLC + lot)" done={!!currentLine.label_photo_url}>
              {currentLine.label_photo_url && (
                <img
                  src={currentLine.label_photo_url}
                  alt="Étiquette"
                  className="w-full max-h-40 object-contain rounded-xl border border-black/[.06] mb-3 bg-gray-50"
                />
              )}
              {extractingLineId === currentLine.id && (
                <p className="text-sm text-blue-600 mb-2">🔎 Lecture de l’étiquette…</p>
              )}
              {draft.labelInfo && (
                <p className="text-sm text-gray-600 mb-2">
                  {draft.labelInfo.dlc
                    ? `${draft.labelInfo.date_type === 'DDM' ? 'DDM' : 'DLC'} lue : ${draft.labelInfo.dlc}`
                    : 'Aucune date lisible'}
                  {draft.labelInfo.lot_number ? ` · lot ${draft.labelInfo.lot_number}` : ''}
                  <span className="text-gray-400"> ({draft.labelInfo.source === 'local' ? 'OCR local' : 'cloud'} — à vérifier)</span>
                </p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => setWebcamOpen(true)}
                  disabled={!!uploadingPhotoLineId}
                  className="flex-1 h-12 rounded-xl bg-gray-900 text-white font-semibold
                    disabled:opacity-50 active:scale-[.98] transition-transform select-none touch-manipulation"
                >
                  {uploadingPhotoLineId === currentLine.id ? 'Envoi…' : '📸 Prendre la photo'}
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!!uploadingPhotoLineId}
                  className="h-12 px-4 rounded-xl bg-white border border-black/[.08] text-gray-600 font-semibold
                    disabled:opacity-50 active:scale-[.98] transition-transform select-none touch-manipulation"
                >
                  Fichier
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void handlePhoto(f);
                }}
              />
            </Step>

            {/* 4 — Vérification DLC / lot / quantité / prix */}
            <Step n={4} title="Vérifier les informations" done={!!draft.dlc}>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-1">
                    Date limite {draft.labelInfo?.dlc && draft.dlc === draft.labelInfo.dlc && (
                      <span className="text-blue-600">📷 lue, à confirmer</span>
                    )}
                  </label>
                  <DLCInput value={draft.dlc} onChange={(v) => patchDraft(currentLine.id, { dlc: v })} />
                  {/* Une DLC par lot : un colis à deux dates se réceptionne en
                      deux lots. On le déclare ici, au moment où on le constate,
                      plutôt que d'aller scinder après coup en mise en rayon. */}
                  {draft.secondLot ? (
                    <div className="mt-2 p-3 rounded-xl bg-amber-50 border border-amber-200 space-y-2">
                      <p className="text-xs font-semibold text-amber-900">Seconde date limite</p>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          value={draft.secondLot.quantity}
                          onChange={(e) => patchDraft(currentLine.id, {
                            secondLot: { ...draft.secondLot!, quantity: e.target.value },
                          })}
                          type="text"
                          inputMode="decimal"
                          data-scan-target
                          placeholder="Quantité"
                          className="h-12 px-3 rounded-xl border border-amber-300 tabular-nums"
                        />
                        <DLCInput
                          value={draft.secondLot.dlc}
                          onChange={(v) => patchDraft(currentLine.id, {
                            secondLot: { ...draft.secondLot!, dlc: v },
                          })}
                        />
                      </div>
                      {/* Deux dates limites viennent souvent avec deux numéros de
                          lot fournisseur — sans ce champ, le jumeau héritait de
                          celui du premier, ce qui fausse la traçabilité. */}
                      <input
                        value={draft.secondLot.lotNumber}
                        onChange={(e) => patchDraft(currentLine.id, {
                          secondLot: { ...draft.secondLot!, lotNumber: e.target.value },
                        })}
                        placeholder="N° de lot du second (optionnel)"
                        data-scan-target
                        className="w-full h-12 px-3 rounded-xl border border-amber-300 text-sm"
                      />
                      <p className="text-xs text-amber-800">
                        Sur les {draft.quantity || '0'} reçus, cette quantité formera un
                        second lot à cette date.
                      </p>
                      <button
                        onClick={() => patchDraft(currentLine.id, { secondLot: null })}
                        className="min-h-[48px] text-xs text-amber-900 underline underline-offset-2
                          select-none touch-manipulation"
                      >
                        Finalement, une seule date
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => patchDraft(currentLine.id, {
                        secondLot: { quantity: '', dlc: '', lotNumber: '' },
                      })}
                      className="mt-2 min-h-[48px] text-sm text-[#2d6a4f] font-semibold underline underline-offset-2
                        select-none touch-manipulation"
                    >
                      ✂️ Ce colis a deux dates limites
                    </button>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-600 mb-1">N° de lot fournisseur</label>
                  <input
                    value={draft.lotNumber}
                    onChange={(e) => patchDraft(currentLine.id, { lotNumber: e.target.value })}
                    placeholder="Optionnel"
                    // Douchette active sur tactile : ce marqueur garantit que la
                    // frappe au clavier virtuel n'est ni interprétée ni effacée.
                    data-scan-target
                    className="w-full h-12 px-3 rounded-xl border border-gray-300"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 mb-1">Quantité reçue</label>
                    <input
                      value={draft.quantity}
                      onChange={(e) => patchDraft(currentLine.id, { quantity: e.target.value })}
                      type="text"
                      inputMode="decimal"
                      data-scan-target
                      className="w-full h-12 px-3 rounded-xl border border-gray-300 tabular-nums"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 mb-1">Prix d’achat</label>
                    <PriceInput
                      valueCents={draft.purchaseCents}
                      onChangeCents={(c) => patchDraft(currentLine.id, {
                        purchaseCents: c,
                        ...(draft.saleEdited ? {} : { saleCents: suggestedSalePriceCents(c) }),
                        prixAchatVerifie: false,
                      })}
                    />
                  </div>
                </div>
                {alerteAchat && (
                  <div className="rounded-xl border-2 border-orange-400 bg-orange-50 p-3 text-sm text-orange-900" role="alert">
                    <p className="font-semibold">
                      ⚠️ {alerteAchat.motif === 'habituel'
                        ? `Prix d’achat ${formatQty(Math.round(alerteAchat.rapport * 10) / 10)} × plus cher que d’habitude (${(alerteAchat.repereCents / 100).toFixed(2)} €)`
                        : `Prix d’achat plus élevé que le prix en caisse (${(alerteAchat.repereCents / 100).toFixed(2)} €)`}
                    </p>
                    <p className="mt-0.5">Est-ce le prix d’un sac ou d’un carton entier ?</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {alerteAchat.diviseur && (
                        <button
                          type="button"
                          onClick={() => diviserPrixAchat(alerteAchat.diviseur!)}
                          className="h-11 px-3 rounded-lg bg-orange-600 text-white font-semibold active:scale-95 transition-transform"
                        >
                          ÷ {formatQty(alerteAchat.diviseur)} → {(Math.round(draft.purchaseCents / alerteAchat.diviseur) / 100).toFixed(2)} €
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => { setPackMode({ mode: 'prix' }); setPackSizeOpen(true); }}
                        className="h-11 px-3 rounded-lg bg-white border border-orange-400 font-semibold active:scale-95 transition-transform"
                      >
                        Contenu du colis…
                      </button>
                      <button
                        type="button"
                        onClick={() => patchDraft(currentLine.id, { prixAchatVerifie: true })}
                        className="h-11 px-3 rounded-lg bg-white border border-gray-300 text-gray-700 font-semibold active:scale-95 transition-transform"
                      >
                        Le prix est juste
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  {(() => {
                    const isExisting = !!currentLine.matched_product_id && !draft.markedAsNew;
                    const current = currentLine.matched_product_price_cents ?? null;
                    const suggested = draft.purchaseCents > 0
                      ? suggestedSalePriceCents(draft.purchaseCents)
                      : 0;
                    const willChange = isExisting && current != null && draft.saleCents !== current;
                    return (
                      <>
                        <label className="block text-sm font-semibold text-gray-600 mb-1">
                          Prix de vente
                          {isExisting && current != null && (
                            <span className="text-gray-400 font-normal">
                              {' '}— en caisse aujourd’hui : {(current / 100).toFixed(2)} €
                            </span>
                          )}
                        </label>
                        <PriceInput
                          valueCents={draft.saleCents}
                          onChangeCents={(c) => patchDraft(currentLine.id, { saleCents: c, saleEdited: true })}
                        />
                        {/* Le conseillé n'est plus PRÉ-REMPLI sur un produit existant :
                            l'afficher dans le champ donnait à croire qu'il s'appliquait,
                            alors que le prix de la caisse ne bouge pas sans confirmation.
                            Il reste proposé, en un tap. */}
                        {isExisting && suggested > 0 && draft.saleCents !== suggested && (
                          <button
                            onClick={() => patchDraft(currentLine.id, { saleCents: suggested, saleEdited: true })}
                            className="mt-1.5 min-h-[48px] text-xs text-[#2d6a4f] font-semibold underline underline-offset-2
                              select-none touch-manipulation"
                          >
                            Appliquer le prix conseillé ({(suggested / 100).toFixed(2)} €)
                          </button>
                        )}
                        {willChange ? (
                          // Rien ne change encore : c'est la question posée à la validation
                          // qui tranche. Annoncer « le prix passera à … » était faux quand
                          // le prix proposé venait d'un prix d'achat modifié.
                          <p className="mt-1.5 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2">
                            Caisse : <span className="font-semibold tabular-nums">{(current! / 100).toFixed(2)} €</span>
                            {' '}→ proposé : <span className="font-semibold tabular-nums">{(draft.saleCents / 100).toFixed(2)} €</span>
                            <br />
                            À la validation, vous choisirez : passer à {(draft.saleCents / 100).toFixed(2)} € ou
                            garder {(current! / 100).toFixed(2)} €.
                          </p>
                        ) : (
                          <p className="mt-1.5 text-xs text-gray-500">
                            {isExisting
                              ? 'Prix inchangé — rien à modifier en rayon.'
                              : 'Deviendra le prix de vente de la nouvelle fiche produit.'}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </Step>

            {/* 5 — Unité / vrac */}
            <Step n={5} title="Unité de vente" done={!!draft.unit}>
              <div className="grid grid-cols-3 gap-2">
                {UNITS.map((u) => (
                  <button
                    key={u.value}
                    onClick={() => {
                      // Passer au Kilo depuis la pièce (ou le colis du bon) sans toucher
                      // au prix laissait le prix du colis comme prix au kilo : on pose
                      // d'office la question du poids d'un colis (demande de Philippe,
                      // 04/11/2026). « Déjà au kilo » referme sans rien changer.
                      const versKilo = u.value === 'kg' && draft.unit !== 'kg';
                      patchDraft(currentLine.id, { unit: u.value, unitTouched: true });
                      if (versKilo) {
                        setPackMode({ mode: 'conversion', depuisUnite: true });
                        setPackSize('');
                        setPackSizeOpen(true);
                      }
                    }}
                    className={`h-12 rounded-xl font-semibold border active:scale-95 transition-transform select-none touch-manipulation
                      ${draft.unit === u.value
                        ? 'bg-[#2d6a4f] text-white border-[#2d6a4f]'
                        : 'bg-white text-gray-700 border-gray-300'}`}
                  >
                    {u.label}
                  </button>
                ))}
              </div>
              {!UNITS.some((u) => u.value === draft.unit) && (
                // Une unité venue du parsing (« colis », « boîte »…) ne correspond à
                // aucun bouton : sans ce rappel, l'écran laisserait croire au défaut.
                <p className="mt-2 text-sm text-amber-700">
                  Unité du bon : <span className="font-semibold">{draft.unit}</span> — choisissez l’unité de vente.
                </p>
              )}
              <button
                onClick={() => { setPackMode({ mode: 'conversion' }); setPackSize(''); setPackSizeOpen(true); }}
                className="mt-2 min-h-11 px-1 text-sm font-medium text-amber-800 underline underline-offset-2
                  select-none touch-manipulation"
              >
                📦→⚖️ Colis vendu au poids (vrac)
              </button>
              {currentLine.matched_product_unit && (
                <p className="mt-2 text-xs text-gray-500">
                  Fiche produit : {currentLine.matched_product_is_weighted || currentLine.matched_product_unit === 'kg'
                    ? 'au poids (kg)' : `à la ${currentLine.matched_product_unit}`}
                </p>
              )}
            </Step>
          </>
        )}
      </main>

      {/* Barre d'action fixe */}
      {!allDone && currentLine && (
        <div className="fixed bottom-0 inset-x-0 z-20 bg-white/95 backdrop-blur border-t border-black/[.08]
          px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="max-w-2xl mx-auto flex gap-3">
            {/* Une facture fournisseur porte souvent plusieurs lignes de consigne :
                passer par le motif de refus ferait deux écrans à chaque fois. */}
            <button
              onClick={() => void handleReject(CONSIGNE_REASON)}
              disabled={busy}
              aria-label="Ligne de consigne ou vidange — ne pas mettre en stock"
              className="h-14 px-3 rounded-2xl bg-white border border-black/[.12] text-gray-700
                flex flex-col items-center justify-center leading-none gap-0.5
                disabled:opacity-50 active:scale-95 transition-transform select-none touch-manipulation"
            >
              <span aria-hidden>♻️</span>
              <span className="text-[10px] font-semibold">Consigne</span>
            </button>
            <button
              onClick={() => setRejectOpen(true)}
              disabled={busy}
              className="h-14 px-4 rounded-2xl bg-white border border-red-200 text-red-600 font-semibold
                disabled:opacity-50 active:scale-95 transition-transform select-none touch-manipulation"
            >
              Refuser
            </button>
            <button
              onClick={() => handleApprove()}
              disabled={busy}
              className="flex-1 h-14 rounded-2xl bg-[#2d6a4f] text-white font-bold text-lg shadow-md
                disabled:opacity-50 active:scale-[.98] transition-transform select-none touch-manipulation"
            >
              {busy ? 'Enregistrement…' : '✓ Réceptionner'}
            </button>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-24 inset-x-0 z-30 flex justify-center px-4 pointer-events-none">
          <p className="bg-gray-900 text-white text-sm font-medium px-4 py-2.5 rounded-full shadow-lg max-w-md text-center">
            {toast}
          </p>
        </div>
      )}

      {/* Modales */}
      <BarcodeScannerModal
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        // Même règle qu'à la douchette : depuis la liste le scan choisit la
        // ligne, dans une ligne ouverte il corrige son rattachement produit.
        onScan={(code) => {
          setScannerOpen(false);
          void (currentLine ? applyScan(code) : scanToSelectLine(code));
        }}
        title={currentLine ? 'Scanner le produit' : 'Scanner un article du bon'}
      />
      <WebcamCaptureModal
        isOpen={webcamOpen}
        onClose={() => setWebcamOpen(false)}
        onCapture={(file) => void handlePhoto(file)}
      />

      {/* Code scanné qui ne correspond à aucune ligne : c'est le cas normal au
          premier passage (les emails de commande ne portent pas d'EAN). On fait
          désigner la ligne, et l'association est mémorisée pour les fois d'après. */}
      {orphanBarcode && (
        <Sheet title="À quelle ligne du bon ?" onClose={() => setOrphanBarcode(null)}>
          <p className="text-sm text-gray-600 mb-3">
            Code <span className="font-mono font-semibold text-gray-900">{orphanBarcode}</span> :
            aucune ligne du bon ne le porte encore. Choisissez la ligne correspondante —
            elle retiendra ce code.
          </p>
          <div className="space-y-2">
            {pendingLines.map((l) => (
              <button
                key={l.id}
                onClick={() => void attachOrphanToLine(l)}
                className="w-full text-left p-3 rounded-xl border border-gray-200 bg-gray-50
                  active:scale-[.98] transition-transform select-none touch-manipulation"
              >
                <span className="block font-semibold text-gray-900 truncate">{l.raw_name}</span>
                {(l.ordered_quantity ?? l.quantity) != null && (
                  <span className="block text-xs text-gray-500">
                    {formatQty((l.ordered_quantity ?? l.quantity) as number)} {l.unit || ''}
                  </span>
                )}
              </button>
            ))}
            {pendingLines.length === 0 && (
              <p className="text-sm text-gray-500">Toutes les lignes de ce bon sont traitées.</p>
            )}
          </div>
        </Sheet>
      )}

      {rejectOpen && (
        <Sheet title="Refuser cette ligne" onClose={() => { setRejectOpen(false); setOtherReason(''); }}>
          <div className="space-y-2">
            {REJECT_REASONS.map((r) => (
              <button
                key={r}
                onClick={() => handleReject(r)}
                className="w-full min-h-[48px] py-2 px-3 rounded-xl bg-gray-50 border border-gray-200 font-semibold
                  text-gray-800 text-left active:scale-[.98] transition-transform select-none touch-manipulation"
              >
                {r}
              </button>
            ))}
            {/* « Autre » : les motifs figés ne couvrent jamais tout, et sans champ
                libre l'opérateur se rabat sur un motif faux — le rapport de refus
                devient alors inexploitable. */}
            <div className="pt-2 border-t border-gray-200">
              <label className="block text-sm font-semibold text-gray-600 mb-1">Autre motif</label>
              <input
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                placeholder="Précisez…"
                data-scan-target
                className="w-full h-12 px-3 rounded-xl border border-gray-300"
              />
              <button
                onClick={() => handleReject(otherReason.trim())}
                disabled={!otherReason.trim()}
                className="mt-2 w-full h-12 rounded-xl bg-gray-900 text-white font-semibold
                  disabled:opacity-40 active:scale-[.98] transition-transform select-none touch-manipulation"
              >
                Refuser avec ce motif
              </button>
            </div>
          </div>
        </Sheet>
      )}

      {packSizeOpen && (
        <Sheet
          title={packMode.mode === 'prix' ? 'Prix d’achat d’un colis' : packMode.depuisUnite ? 'Vendu au kilo' : 'Colis vendu au poids'}
          onClose={() => setPackSizeOpen(false)}
        >
          <p className="text-sm text-gray-600 mb-3">
            {packMode.mode === 'prix'
              ? `Contenu d’un colis (en ${draft?.unit === 'kg' ? 'kg' : draft?.unit || 'unités'}). Seul le prix d’achat est divisé : la quantité reçue ne change pas.`
              : packMode.depuisUnite
                ? `Le bon indique ${draft?.quantity || '?'} à ${((draft?.purchaseCents ?? 0) / 100).toFixed(2)} € : par colis ? Indiquez le poids d’un colis en kg — la quantité sera multipliée et le prix d’achat divisé d’autant.`
                : 'Poids d’un colis, en kg. La quantité sera multipliée et le prix d’achat divisé d’autant.'}
          </p>
          <input
            value={packSize}
            onChange={(e) => setPackSize(e.target.value)}
            type="text"
            inputMode="decimal"
            autoFocus
            placeholder="Ex. 5"
            className="w-full h-14 px-4 rounded-xl border border-gray-300 text-lg tabular-nums"
          />
          <button
            onClick={applyPackSize}
            className="mt-4 w-full h-14 rounded-2xl bg-[#2d6a4f] text-white font-semibold
              active:scale-[.98] transition-transform select-none touch-manipulation"
          >
            {packMode.mode === 'prix' ? 'Diviser le prix d’achat' : 'Convertir en kg'}
          </button>
          {packMode.depuisUnite && (
            <button
              onClick={() => setPackSizeOpen(false)}
              className="mt-3 w-full h-14 rounded-2xl bg-white border border-gray-300 font-semibold text-gray-700
                active:scale-[.98] transition-transform select-none touch-manipulation"
            >
              Non : quantité et prix sont déjà au kilo
            </button>
          )}
        </Sheet>
      )}

      {invitationNouveaute.element}
      {confirm && (
        <Sheet title={confirm.title} onClose={() => setConfirm(null)}>
          <p className="text-sm text-gray-700 whitespace-pre-line mb-4">{confirm.message}</p>
          {confirm.secondaryLabel && confirm.onSecondary && (
            <button
              onClick={confirm.onSecondary}
              className="w-full h-14 mb-3 rounded-2xl bg-gray-100 border border-gray-300 font-semibold text-gray-800
                active:scale-[.98] transition-transform select-none touch-manipulation"
            >
              {confirm.secondaryLabel}
            </button>
          )}
          <div className="flex gap-3">
            <button
              onClick={() => setConfirm(null)}
              className="flex-1 h-14 rounded-2xl bg-white border border-gray-300 font-semibold text-gray-700
                active:scale-[.98] transition-transform select-none touch-manipulation"
            >
              Annuler
            </button>
            <button
              onClick={confirm.onConfirm}
              className="flex-1 h-14 rounded-2xl bg-[#2d6a4f] text-white font-semibold
                active:scale-[.98] transition-transform select-none touch-manipulation"
            >
              {confirm.confirmLabel || 'Confirmer'}
            </button>
          </div>
        </Sheet>
      )}
    </div>
  );
}

/** Bloc d'étape numéroté : la pastille verte dit d'un coup d'œil ce qui est fait. */
function Step({ n, title, done, optional, children }: {
  n: number; title: string; done: boolean; optional?: boolean; children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-2xl border border-black/[.08] shadow-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-sm font-bold
          ${done ? 'bg-[#2d6a4f] text-white' : 'bg-gray-100 text-gray-500'}`}>
          {done ? '✓' : n}
        </span>
        <h3 className="font-bold text-gray-900">{title}</h3>
        {optional && !done && (
          <span className="text-xs text-gray-400 font-medium">facultatif</span>
        )}
      </div>
      {children}
    </section>
  );
}

/** Feuille modale ancrée en bas — plus fiable que window.confirm/prompt en PWA installée. */
function Sheet({ title, onClose, children }: {
  title: string; onClose: () => void; children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl p-5 max-h-[90vh] overflow-y-auto
        pb-[max(1.25rem,env(safe-area-inset-bottom))]">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="w-11 h-11 rounded-full bg-gray-100 active:scale-95 select-none touch-manipulation"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
